import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'
import ProtectedRoute from './components/ProtectedRoute'

// Public pages
import LandingPage from './pages/LandingPage'
import Login from './pages/auth/Login'
import SchoolRegistration from './pages/SchoolRegistration'
import Settings from './pages/Settings'

// Demo pages
import TeacherDemo from './pages/demo/TeacherDemo'
import ParentDemo from './pages/demo/ParentDemo'
import StudentDemo from './pages/demo/StudentDemo'
import SuperAdminDemo from './pages/demo/SuperAdminDemo'

// Dashboards
import SuperAdminDashboard from './pages/dashboards/SuperAdminDashboard'
import SchoolAdminDashboard from './pages/dashboards/SchoolAdminDashboard'
import TeacherDashboard from './pages/dashboards/TeacherDashboard'
import ParentDashboard from './pages/dashboards/ParentDashboard'
import StudentDashboard from './pages/dashboards/StudentDashboard'

function App() {
  const { user, profile, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center gradient-mesh">
        <div className="flex flex-col items-center">
          <div className="h-12 w-12 rounded-xl gradient-primary flex items-center justify-center shadow-glow animate-pulse">
            <svg className="h-6 w-6 text-white animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <p className="mt-4 text-sm text-gray-500 font-medium">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={user ? <Navigate to={getDashboardRoute(profile)} /> : <LandingPage />} />
      <Route path="/login" element={user ? <Navigate to={getDashboardRoute(profile)} /> : <Login />} />
      <Route path="/register-school" element={<SchoolRegistration />} />

      {/* Super Admin routes */}
      <Route
        path="/admin/super/*"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <SuperAdminDashboard />
          </ProtectedRoute>
        }
      />

      {/* School Admin routes */}
      <Route
        path="/admin/school/*"
        element={
          <ProtectedRoute allowedRoles={['school_admin']}>
            <SchoolAdminDashboard />
          </ProtectedRoute>
        }
      />

      {/* Teacher routes */}
      <Route
        path="/teacher/*"
        element={
          <ProtectedRoute allowedRoles={['teacher']}>
            <TeacherDashboard />
          </ProtectedRoute>
        }
      />

      {/* Parent routes */}
      <Route
        path="/parent/*"
        element={
          <ProtectedRoute allowedRoles={['parent']}>
            <ParentDashboard />
          </ProtectedRoute>
        }
      />

      {/* Student routes */}
      <Route
        path="/student/*"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <StudentDashboard />
          </ProtectedRoute>
        }
      />

      {/* Settings route - accessible to all authenticated users */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'school_admin', 'teacher', 'parent', 'student']}>
            <Settings />
          </ProtectedRoute>
        }
      />

      {/* Demo routes - public */}
      <Route path="/demo/teacher" element={<TeacherDemo />} />
      <Route path="/demo/parent" element={<ParentDemo />} />
      <Route path="/demo/student" element={<StudentDemo />} />
      <Route path="/demo/admin" element={<SuperAdminDemo />} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

function getDashboardRoute(profile) {
  switch (profile?.role) {
    case 'super_admin':
      return '/admin/super'
    case 'school_admin':
      return '/admin/school'
    case 'teacher':
      return '/teacher'
    case 'parent':
      return '/parent'
    case 'student':
      return '/student'
    default:
      return '/'
  }
}

export default App